import { config } from './config';
import type { PublicSession, SessionStatus } from './api/types';

/**
 * postMessage bridge used when pay.coinvello.com is rendered inside the
 * @coinvello/checkout iframe. Messages are namespaced and carry the session id.
 * The parent origin is taken from `?origin=` (set by the SDK) and must either
 * be in VITE_CV_ALLOWED_EMBED_ORIGINS or, when that list is empty, any https origin.
 */
export type BridgeMessage =
  | { source: 'coinvello'; type: 'ready'; sessionId: string }
  | { source: 'coinvello'; type: 'resize'; sessionId: string; height: number }
  | { source: 'coinvello'; type: 'status'; sessionId: string; status: SessionStatus; session: PublicSession }
  | { source: 'coinvello'; type: 'paid'; sessionId: string; session: PublicSession }
  | { source: 'coinvello'; type: 'expired'; sessionId: string; session: PublicSession }
  | { source: 'coinvello'; type: 'cancel'; sessionId: string }
  | { source: 'coinvello'; type: 'error'; sessionId: string; message: string };

const params = new URLSearchParams(location.search);
const parentOrigin = params.get('origin') ?? '';

export const isEmbedded = (() => {
  try {
    return window.self !== window.top && !!parentOrigin;
  } catch {
    return true;
  }
})();

function originAllowed(origin: string): boolean {
  if (!origin) return false;
  if (config.allowedEmbedOrigins.length) return config.allowedEmbedOrigins.includes(origin);
  return origin.startsWith('https://') || origin.startsWith('http://localhost');
}

export function post(msg: BridgeMessage): void {
  if (!isEmbedded || !originAllowed(parentOrigin)) return;
  try {
    window.parent.postMessage(msg, parentOrigin);
  } catch {
    /* ignore */
  }
}

export interface ParentHandlers {
  onTheme(theme: 'light' | 'dark' | 'auto'): void;
  /** Host asked for a UI language (BCP-47). */
  onLocale?(locale: string): void;
  /** Host asked for a display currency (ISO 4217). */
  onCurrency?(currency: string): void;
}

const currencyListeners = new Set<(c: string) => void>();
/** Subscribe to host-driven display-currency changes (the App owns the state). */
export function onHostCurrency(fn: (c: string) => void): () => void {
  currencyListeners.add(fn);
  return () => currencyListeners.delete(fn);
}

/** Listen for parent commands (theme, locale, currency). Only accepts the configured parent origin. */
export function listenParent(h: ParentHandlers): void {
  if (!isEmbedded) return;
  window.addEventListener('message', (ev) => {
    if (ev.origin !== parentOrigin) return;
    const d = ev.data as { source?: string; type?: string; theme?: string; locale?: string; currency?: string } | null;
    if (!d || d.source !== 'coinvello-host') return;
    if (d.type === 'theme' && (d.theme === 'light' || d.theme === 'dark' || d.theme === 'auto')) h.onTheme(d.theme);
    if (d.type === 'locale' && typeof d.locale === 'string' && /^[A-Za-z]{2,3}([-_][A-Za-z0-9]{2,8})*$/.test(d.locale)) h.onLocale?.(d.locale);
    if (d.type === 'currency' && typeof d.currency === 'string' && /^[A-Za-z]{3}$/.test(d.currency)) {
      h.onCurrency?.(d.currency.toUpperCase());
      for (const fn of currencyListeners) fn(d.currency.toUpperCase());
    }
  });
}

/** Report content height so the host iframe can auto-size. */
export function startResizeReporting(sessionId: string): void {
  if (!isEmbedded) return;
  let last = 0;
  const report = () => {
    const h = Math.ceil(document.documentElement.scrollHeight);
    if (h !== last) {
      last = h;
      post({ source: 'coinvello', type: 'resize', sessionId, height: h });
    }
  };
  new ResizeObserver(report).observe(document.body);
  report();
}
